import featureFlagService, { FEATURES } from '../services/featureFlagService';
import { getOrCreateJourneyAnonymousId } from './journeyIdentity';

export const PROVIDER_LANDING_EXPERIMENT = 'provider_service_landing_v1';
export const PROVIDER_LANDING_ASSIGNMENT_KEY = 'rxr_provider_landing_assignment_v1';
export const PROVIDER_LANDING_EXPOSURE_KEY = 'rxr_provider_landing_exposure_v1';
export const PROVIDER_LANDING_ATTRIBUTION_MS = 7 * 24 * 60 * 60 * 1000;

const DEFAULT_ASSIGNMENT = { variant: 'account_first', experimentVariant: null };
const isVariant = (value) => value === 'account_first' || value === 'service_first';
const experimentVariantFor = (variant) => `${PROVIDER_LANDING_EXPERIMENT}:${variant}`;
const isAssignment = (value) => isVariant(value?.variant) && (
  value.experimentVariant === experimentVariantFor(value.variant) ||
  (value.variant === 'account_first' && value.experimentVariant === null)
);

// A versioned deterministic bucket makes reloads and new tabs agree. The
// persisted record also pins the assignment if the bucketing code evolves.
function bucket(anonymousId) {
  const input = `${PROVIDER_LANDING_EXPERIMENT}:${anonymousId}`;
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash = Math.imul(hash ^ input.charCodeAt(i), 16777619);
  }
  return (hash >>> 0) % 2;
}

export function getProviderLandingAssignment() {
  try {
    if (!featureFlagService.isFeatureEnabled(FEATURES.PROVIDER_SERVICE_LANDING_SPLIT_TEST)) {
      return { ...DEFAULT_ASSIGNMENT };
    }
    const anonymousId = getOrCreateJourneyAnonymousId();
    const saved = JSON.parse(localStorage.getItem(PROVIDER_LANDING_ASSIGNMENT_KEY) || 'null');
    if (saved?.anonymousId === anonymousId && isAssignment(saved) && saved.experimentVariant) {
      return { variant: saved.variant, experimentVariant: saved.experimentVariant };
    }
    const variant = bucket(anonymousId) === 0 ? 'account_first' : 'service_first';
    const assignment = { variant, experimentVariant: experimentVariantFor(variant) };
    localStorage.setItem(PROVIDER_LANDING_ASSIGNMENT_KEY, JSON.stringify({ ...assignment, anonymousId }));
    return assignment;
  } catch {
    // Without durable storage we cannot promise stable randomization. Keep
    // the page usable and exclude this visit from the split-test cohort.
    return { ...DEFAULT_ASSIGNMENT };
  }
}

/** Read only. Importing this helper never creates ids, assigns, or emits. */
export function getProviderLandingContext() {
  try {
    const saved = JSON.parse(localStorage.getItem(PROVIDER_LANDING_EXPOSURE_KEY) || 'null');
    const now = Date.now();
    const accountCreatedAt = saved?.accountCreatedAt;
    const validAccountAnchor = Number.isFinite(accountCreatedAt) && accountCreatedAt >= saved.exposedAt &&
      accountCreatedAt - saved.exposedAt < PROVIDER_LANDING_ATTRIBUTION_MS && accountCreatedAt <= now;
    const anchor = validAccountAnchor ? accountCreatedAt : saved?.exposedAt;
    if (!isAssignment(saved) || !Number.isFinite(saved.exposedAt) ||
        saved.exposedAt > now || now - anchor >= PROVIDER_LANDING_ATTRIBUTION_MS) return null;
    return {
      variant: saved.variant, experimentVariant: saved.experimentVariant, exposedAt: saved.exposedAt,
      ...(validAccountAnchor ? { accountCreatedAt } : {}),
    };
  } catch {
    return null;
  }
}

/** Call only after the flagged landing is actually displayed. */
export function recordProviderLandingExposure(assignment) {
  if (!isAssignment(assignment)) return null;
  try {
    const existing = getProviderLandingContext();
    // Repeated visits do not slide the seven-day attribution window forward.
    if (existing?.variant === assignment.variant && existing.experimentVariant === assignment.experimentVariant) {
      return existing;
    }
    const context = { variant: assignment.variant, experimentVariant: assignment.experimentVariant, exposedAt: Date.now() };
    localStorage.setItem(PROVIDER_LANDING_EXPOSURE_KEY, JSON.stringify(context));
    return context;
  } catch {
    return null;
  }
}

/** Called by the existing successful-registration milestone, not a CTA. */
export function markProviderLandingAccountCreated() {
  const context = getProviderLandingContext();
  if (!context || context.accountCreatedAt) return context;
  const updated = { ...context, accountCreatedAt: Date.now() };
  try {
    localStorage.setItem(PROVIDER_LANDING_EXPOSURE_KEY, JSON.stringify(updated));
    return updated;
  } catch {
    return context;
  }
}

export function providerLandingAnalyticsParams(context = getProviderLandingContext()) {
  if (!context) return {};
  return {
    provider_landing_variant: context.variant,
    provider_landing_exposed_at: context.exposedAt,
    // Null explicitly separates the rollout from randomized experiment data.
    experiment_provider_service_landing: context.experimentVariant,
  };
}
